import gql from "graphql-tag";
import { AuthService } from "@/lib/auth";
import GraphQLClient, { RequestParam } from "../api";
import { User, ConnectedOtherServices, SegmentType, UpdateUserInput } from "../models";

interface IUserClient {
  fetchUser: () => Promise<User>;
  checkConnectedOtherServices: () => Promise<ConnectedOtherServices>;
  updateUser: (input: string | UpdateUserInput) => Promise<User>;
  deleteUser: (email: string) => Promise<User>;
  startNolaAgentFeature: () => Promise<User>;
}

export class UserClient implements IUserClient {
  async fetchUser(): Promise<User> {
    const { user } = await GraphQLClient.request({ query: fetchUserQuery });
    return user;
  }

  async checkConnectedOtherServices(): Promise<ConnectedOtherServices> {
    const { checkConnectedOtherServices } = await GraphQLClient.request({ query: checkConnectedOtherServicesQuery });
    return checkConnectedOtherServices;
  }

  /**
   * ユーザー情報を更新
   * @param input ペンネーム文字列、または UpdateUserInput（商業化希望など）
   */
  async updateUser(input: string | UpdateUserInput): Promise<User> {
    const updateInput: UpdateUserInput = typeof input === "string" ? { name: input } : input;
    const params: RequestParam = {
      query: updateUserQuery,
      variables: {
        input: updateInput,
      },
    };
    const { updateUser } = await GraphQLClient.request(params);
    return updateUser;
  }

  async deleteUser(email: string): Promise<User> {
    const params: RequestParam = {
      query: deleteUserQuery,
      variables: {
        input: {
          delete: true,
          email,
        },
      },
    };
    const { deleteUser } = await GraphQLClient.request(params);
    return deleteUser;
  }

  async startNolaAgentFeature(): Promise<User> {
    const params: RequestParam = {
      query: startNolaAgentFeatureQuery,
      variables: {},
    };
    const { startNolaAgentFeature } = await GraphQLClient.request(params);
    return startNolaAgentFeature;
  }

  async fetchListSegment(): Promise<SegmentType[]> {
    const service = new AuthService();
    const token = await service.getJwtToken();
    const { listSegment } = await GraphQLClient.request({
      query: fetchListSegmentQuery,
      headers: { "x-jwt-token": token },
    });
    return listSegment.items;
  }
}

/**
 * Query
 */

const fetchUserQuery = gql`
  {
    user {
      userId
      name
      agentStartedAt
      wantsCommercialization
    }
  }
`;

const checkConnectedOtherServicesQuery = gql`
  {
    checkConnectedOtherServices {
      nolaPublishing {
        result
        error
      }
      nolaNovel {
        result
        error
      }
    }
  }
`;

const fetchListSegmentQuery = gql`
  {
    listSegment {
      items
    }
  }
`;

/** Mutation */

const updateUserQuery = gql`
  mutation UpdateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      userId
      name
      agentStartedAt
      wantsCommercialization
    }
  }
`;

const deleteUserQuery = gql`
  mutation DeleteUser($input: DeleteUserInput!) {
    deleteUser(input: $input) {
      userId
      name
    }
  }
`;

const startNolaAgentFeatureQuery = gql`
  mutation StartNolaAgentFeature {
    startNolaAgentFeature {
      userId
      name
      agentStartedAt
      wantsCommercialization
    }
  }
`;
